import { memo } from "react";
import type { BuildingProperties } from "../map/layers";
import { StreetViewThumb } from "./StreetViewThumb";

interface Props {
  building: BuildingProperties | null;
  /** [lng, lat] where the building was picked — drives the Street View thumbnail. */
  coord?: [number, number] | null;
  onClose: () => void;
}

/**
 * Right-anchored card showing the picked building's metadata.
 * Closes on backdrop click or ESC (wired in App.tsx).
 */
function BuildingCardImpl({ building, coord, onClose }: Props) {
  if (!building) return null;
  const name = building.nameEn || building.name || building.nameTh || "Untitled building";
  const altName = building.nameTh && building.nameTh !== name ? building.nameTh : null;

  return (
    <aside className="building-card" role="dialog" aria-label={`Building: ${name}`}>
      <header className="building-card-head">
        <div>
          <span className="eyebrow mono">KMITL · BUILDING</span>
          <h3 className="building-card-title">{name}</h3>
          {altName && <div className="building-card-alt">{altName}</div>}
        </div>
        <button onClick={onClose} aria-label="Close" className="building-card-close mono">
          ESC
        </button>
      </header>
      <dl className="building-card-meta mono">
        {building.building && building.building !== "yes" && (
          <>
            <dt>TYPE</dt>
            <dd>{building.building}</dd>
          </>
        )}
        {building.levels != null && (
          <>
            <dt>LEVELS</dt>
            <dd>{building.levels}</dd>
          </>
        )}
        {building.height != null && (
          <>
            <dt>HEIGHT</dt>
            <dd>{building.height} m</dd>
          </>
        )}
        {building.operator && (
          <>
            <dt>OPERATOR</dt>
            <dd>{building.operator}</dd>
          </>
        )}
        <dt>OSM</dt>
        <dd className="mono">{building.id}</dd>
      </dl>

      {/* ── Ground truth: Google Street View at this location ── */}
      {coord && (
        <div className="building-card-section">
          <span className="eyebrow mono">STREET VIEW</span>
          <StreetViewThumb coord={coord} />
        </div>
      )}
    </aside>
  );
}

export const BuildingCard = memo(BuildingCardImpl);
